/**
 * Documents Settings Page
 * Admin settings for the Document Management System.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Settings, Shield, FolderTree, Copy, Loader2, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DocumentPermissionsManager } from '../components/settings/DocumentPermissionsManager';
import { getDuplicates, resolveDuplicate } from '../api';
import type { DuplicateGroup } from '../api';

export function DocumentsSettingsPage() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background">
      {/* Header */}
      <div className="bg-white dark:bg-card border-b border-gray-200 dark:border-border px-6 py-4">
        <div className="flex items-center gap-4">
          <Link
            to="/documents"
            className="flex items-center gap-2 text-sm text-gray-600 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Documents
          </Link>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <div className="p-2 bg-gray-100 dark:bg-muted rounded-lg">
            <Settings className="h-6 w-6 text-gray-600 dark:text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">
              Document Settings
            </h1>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
              Manage document access, permissions, and scoping rules
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* User Permissions — live */}
        <DocumentPermissionsManager />

        {/* Duplicate Cleanup */}
        <DuplicateCleanup />

        {/* Access Scoping — placeholder for classification-based scoping */}
        <div className="bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-5 w-5 text-gray-500 dark:text-muted-foreground" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">Access Scoping</h2>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">Pre-filter documents by classification for each user or group</p>
            </div>
          </div>
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-border p-8 text-center">
            <Shield className="h-10 w-10 text-gray-300 dark:text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-muted-foreground">Coming after classification</p>
            <p className="text-xs text-gray-400 dark:text-muted-foreground/60 mt-1 max-w-sm mx-auto">
              Define scoping rules based on document classification, tags, and metadata to pre-filter what each user or group can see.
            </p>
          </div>
        </div>

        {/* Classification Configuration — placeholder */}
        <div className="bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border p-6">
          <div className="flex items-center gap-3 mb-4">
            <FolderTree className="h-5 w-5 text-gray-500 dark:text-muted-foreground" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">Classification Configuration</h2>
              <p className="text-sm text-gray-500 dark:text-muted-foreground">Configure automatic document classification rules</p>
            </div>
          </div>
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-border p-8 text-center">
            <FolderTree className="h-10 w-10 text-gray-300 dark:text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-500 dark:text-muted-foreground">Coming soon</p>
            <p className="text-xs text-gray-400 dark:text-muted-foreground/60 mt-1 max-w-sm mx-auto">
              Define rules for automatic document classification, tagging, and metadata extraction.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Duplicate Cleanup Component ---

function DuplicateCleanup() {
  const queryClient = useQueryClient();
  const [resolved, setResolved] = useState(0);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['dms-duplicates'],
    queryFn: getDuplicates,
  });

  const groups: DuplicateGroup[] = data?.data ?? [];
  const totalDuplicates = groups.reduce((sum, g) => sum + g.documents.length - 1, 0);

  const resolveAllMutation = useMutation({
    mutationFn: async () => {
      let deleted = 0;
      for (const group of groups) {
        // Keep the oldest document (most links/versions), delete the rest
        const sorted = [...group.documents].sort((a, b) => {
          // Prefer the one with more links, then more versions, then oldest
          if (b.linkCount !== a.linkCount) return b.linkCount - a.linkCount;
          if (b.versionCount !== a.versionCount) return b.versionCount - a.versionCount;
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        });
        const keep = sorted[0];
        const deleteIds = sorted.slice(1).map((d) => d.documentId);

        const result = await resolveDuplicate({
          action: 'keep',
          keepDocumentId: keep.documentId,
          deleteDocumentIds: deleteIds,
        });
        deleted += result.deletedCount;
        setResolved((prev) => prev + result.deletedCount);
      }
      return deleted;
    },
    onSuccess: (deleted) => {
      toast.success(`Cleaned up ${deleted} duplicate document${deleted !== 1 ? 's' : ''}`);
      setResolved(0);
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      refetch();
    },
    onError: (error) => {
      toast.error('Cleanup failed', { description: error instanceof Error ? error.message : 'Unknown error' });
      setResolved(0);
    },
  });

  return (
    <div className="bg-white dark:bg-card rounded-lg border border-gray-200 dark:border-border p-6">
      <div className="flex items-center gap-3 mb-4">
        <Copy className="h-5 w-5 text-gray-500 dark:text-muted-foreground" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">Duplicate Cleanup</h2>
          <p className="text-sm text-gray-500 dark:text-muted-foreground">
            Find and remove documents with identical file content (same SHA-256 hash)
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning for duplicates...
        </div>
      ) : groups.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-green-600 py-4">
          <CheckCircle2 className="h-4 w-4" /> No duplicates found
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            {groups.map((group) => (
              <div key={group.checksum} className="rounded-md border p-3">
                <p className="text-xs font-mono text-muted-foreground mb-2">
                  SHA-256: {group.checksum.slice(0, 16)}...
                </p>
                <div className="space-y-1">
                  {group.documents.map((doc, i) => (
                    <div key={doc.documentId} className="flex items-center gap-2 text-sm">
                      {i === 0 ? (
                        <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded font-medium">keep</span>
                      ) : (
                        <span className="text-xs bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 px-1.5 py-0.5 rounded font-medium">delete</span>
                      )}
                      <span className="truncate">{doc.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {doc.linkCount > 0 && `${doc.linkCount} links · `}
                        {doc.versionCount > 1 && `v${doc.versionCount} · `}
                        {new Date(doc.createdAt).toLocaleDateString('en-CA')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="destructive"
            size="sm"
            disabled={resolveAllMutation.isPending}
            onClick={() => resolveAllMutation.mutate()}
          >
            {resolveAllMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Cleaning up ({resolved}/{totalDuplicates})...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Remove {totalDuplicates} duplicate{totalDuplicates !== 1 ? 's' : ''}
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
